import React, { useState, useEffect } from 'react';
import { Edit, Plus, Save, X, Check, AlertCircle } from 'lucide-react';
import { User, UserRole, Product } from '../lib/types';
import { getProducts, saveProducts } from '../lib/products';

interface DashboardCatalogueProps {
  user: User;
}

const DashboardCatalogue: React.FC<DashboardCatalogueProps> = ({ user }) => {
  const [products, setProducts] = useState<Product[]>(getProducts);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if user is admin
  if (user.role !== 'admin') {
    return (
      <div className="text-center py-10">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Accès restreint</h3>
        <p className="text-gray-600">
          Vous n'avez pas les droits nécessaires pour accéder à cette page.
        </p>
      </div>
    );
  }

  const handleEdit = (product: Product) => {
    setEditingProduct({ ...product });
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingProduct({
      id: `product-${Date.now()}`,
      name: '',
      price: '',
      features: [],
      stripePriceId: '',
      maxUsers: 1,
      maxPosts: 0,
      role: 'trial'
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingProduct) return;

    try {
      // Validate required fields
      if (!editingProduct.name || !editingProduct.price || !editingProduct.stripePriceId) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }

      // Update products list
      const updatedProducts = products.map(p => 
        p.id === editingProduct.id ? editingProduct : p
      );

      if (!products.find(p => p.id === editingProduct.id)) {
        updatedProducts.push(editingProduct);
      }

      setProducts(updatedProducts);
      saveProducts(updatedProducts);
      
      setSuccess('Produit sauvegardé avec succès !');
      setTimeout(() => {
        setSuccess(null);
        setIsModalOpen(false);
      }, 2000);
    } catch (error: any) {
      setError(error.message);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleFeatureChange = (index: number, value: string) => {
    if (!editingProduct) return;

    const newFeatures = [...editingProduct.features];
    newFeatures[index] = value;
    setEditingProduct({ ...editingProduct, features: newFeatures });
  };

  const addFeature = () => {
    if (!editingProduct) return;
    setEditingProduct({
      ...editingProduct,
      features: [...editingProduct.features, '']
    });
  };

  const removeFeature = (index: number) => {
    if (!editingProduct) return;
    const newFeatures = editingProduct.features.filter((_, i) => i !== index);
    setEditingProduct({ ...editingProduct, features: newFeatures });
  };

  return (
    <div className="flex-1 p-6 bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Catalogue des produits</h2>
        <button
          onClick={handleCreate}
          className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nouveau produit</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
          <Check className="w-5 h-5 mr-2" />
          <p>{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {products.map(product => (
          <div key={product.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">{product.name}</h3>
                <p className="text-2xl font-bold text-indigo-600 mt-2">{product.price}€ <span className="text-sm text-gray-500">/mois</span></p>
              </div>
              <button
                onClick={() => handleEdit(product)}
                className="text-gray-500 hover:text-indigo-600 transition-colors"
              >
                <Edit className="w-5 h-5" />
              </button>
            </div>

            <ul className="space-y-2 mb-4">
              {product.features.map((feature, index) => (
                <li key={index} className="flex items-center text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="text-sm text-gray-500 mt-4">
              <p>ID Stripe : {product.stripePriceId}</p>
              <p>Utilisateurs max : {product.maxUsers}</p>
              <p>Offres : {product.maxPosts}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                {editingProduct.id.startsWith('product-') ? 'Nouveau produit' : 'Modifier le produit'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
                <Check className="w-5 h-5 mr-2" />
                <p>{success}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du produit *
                </label>
                <input
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: Pro"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prix mensuel (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingProduct.price}
                  onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="14.90"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID du prix Stripe *
                </label>
                <input
                  type="text"
                  value={editingProduct.stripePriceId}
                  onChange={(e) => setEditingProduct({ ...editingProduct, stripePriceId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="price_xxxxx"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre maximum d'utilisateurs
                </label>
                <input
                  type="number"
                  value={editingProduct.maxUsers === 'Illimité' ? '' : editingProduct.maxUsers}
                  onChange={(e) => setEditingProduct({ ...editingProduct, maxUsers: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  min="1"
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fonctionnalités
                </label>
                <div className="space-y-2">
                  {editingProduct.features.map((feature, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Ex: Génération IA"
                      />
                      <button
                        onClick={() => removeFeature(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addFeature}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                  >
                    + Ajouter une fonctionnalité
                  </button>
                </div>
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCatalogue;